import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { MapPin, Calendar, Clock, Users, TrendingUp, Loader2, ExternalLink } from 'lucide-react'
import { Modal } from '@meiuca/ui-components'
import { formatCurrency, formatDateForUI, formatTimeForUI } from '@meiuca/web-foundation'
import { modalitiesManager } from '@/managers/modalitiesManager'
import { useAuth } from '@/contexts/AuthContext'
import { AuthModal } from '@/components/AuthModal'
import { dayUseService } from '@/services/dayUseService'
import { analyticsService } from '@/lib/firebase'
import { ANALYTICS_EVENTS } from '@/constants/analyticsEvents'
import type { DayUseOccurrenceDetail } from '@/types/day-use-occurrence'

interface DayUseOccurrenceDetailModalProps {
  occurrenceId: string | null
  isOpen: boolean
  onClose: () => void
}

export function DayUseOccurrenceDetailModal({ occurrenceId, isOpen, onClose }: DayUseOccurrenceDetailModalProps) {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState<DayUseOccurrenceDetail | null>(null)
  const [actionLoading, setActionLoading] = useState(false)
  const [showLoginModal, setShowLoginModal] = useState(false)

  useEffect(() => {
    if (!occurrenceId || !isOpen) {
      setLoading(false)
      return
    }

    const loadData = async () => {
      try {
        setLoading(true)
        const result = await dayUseService.getDayUseOccurrenceDetail(occurrenceId)
        setData(result)
        analyticsService.track(ANALYTICS_EVENTS.DAY_USE_DETAIL_VIEWED, {
          occurrence_id: occurrenceId ?? undefined,
          venue_id: result.venue.id,
          modality: result.occurrence.modality,
        })
      } catch (error) {
        console.error('Error loading occurrence detail:', error)
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [occurrenceId, isOpen, isAuthenticated]) // Reload when authentication status changes

  const handleParticipate = async () => {
    if (!data || !occurrenceId) return

    analyticsService.track(ANALYTICS_EVENTS.DAY_USE_JOIN_CLICKED, {
      occurrence_id: occurrenceId,
      venue_id: data.venue.id,
    })

    if (!isAuthenticated) {
      analyticsService.track(ANALYTICS_EVENTS.DAY_USE_JOIN_LOGIN_REQUIRED, {
        occurrence_id: occurrenceId,
      })
      setShowLoginModal(true)
      return
    }

    setActionLoading(true)
    try {
      await dayUseService.joinDayUseOccurrence(occurrenceId)
      analyticsService.track(ANALYTICS_EVENTS.DAY_USE_JOIN_COMPLETED, {
        occurrence_id: occurrenceId,
        venue_id: data.venue.id,
        modality: data.occurrence.modality,
        price_cents: data.occurrence.pricePerPersonCents,
      })
      const updated = await dayUseService.getDayUseOccurrenceDetail(occurrenceId)
      setData(updated)
    } catch (error) {
      analyticsService.track(ANALYTICS_EVENTS.DAY_USE_JOIN_ERROR, {
        occurrence_id: occurrenceId,
        error_message: error instanceof Error ? error.message : 'Unknown error',
      })
      console.error('Error participating:', error)
    } finally {
      setActionLoading(false)
    }
  }

  const handleCancelParticipation = async () => {
    if (!data || !occurrenceId) return

    setActionLoading(true)
    try {
      await dayUseService.leaveDayUseOccurrence(occurrenceId)
      const updated = await dayUseService.getDayUseOccurrenceDetail(occurrenceId)
      setData(updated)
    } catch (error) {
      console.error('Error canceling participation:', error)
    } finally {
      setActionLoading(false)
    }
  }

  if (loading) {
    return (
      <Modal open={isOpen} onClose={onClose} title="Carregando..." size="lg">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-brand-primary" />
        </div>
      </Modal>
    )
  }

  if (!data) {
    return (
      <Modal open={isOpen} onClose={onClose} title="Erro" size="md">
        <div className="text-center py-4">
          <p className="text-text-secondary mb-4">Ocorrência não encontrada</p>
          <button
            onClick={onClose}
            className="w-full py-2 px-4 bg-brand-primary text-white rounded-lg hover:bg-brand-primary-dark transition-colors"
          >
            Fechar
          </button>
        </div>
      </Modal>
    )
  }

  const { venue, occurrence } = data
  const modalityLabel = modalitiesManager.getModalityDescription(occurrence.modality)

  const occupancyPercentage = occurrence.maxPeople
    ? Math.round((occurrence.totalParticipants / occurrence.maxPeople) * 100)
    : 100

  const isAlmostFull = occupancyPercentage >= 80
  const isFull = occurrence.availableSpots === 0
  const isUnlimited = !occurrence.maxPeople

  const handleVenueClick = () => {
    onClose()
    navigate(`/arena/${venue.id}`)
  }

  const handleAddressClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (venue.latitude && venue.longitude) {
      window.open(`https://www.google.com/maps/search/?api=1&query=${venue.latitude},${venue.longitude}`, '_blank')
    } else {
      const addressQuery = encodeURIComponent(`${venue.address}${venue.city ? `, ${venue.city}` : ''}${venue.state ? `, ${venue.state}` : ''}`)
      window.open(`https://www.google.com/maps/search/?api=1&query=${addressQuery}`, '_blank')
    }
  }

  return (
    <Modal open={isOpen} onClose={onClose} title="Day Use" size="lg">
      <div className="flex flex-col max-h-[60vh]">
        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto space-y-6 pr-2 -mr-2">
        {/* Venue Info */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-start gap-4">
            {venue.logoUrl && (
              <img
                src={venue.logoUrl}
                alt={venue.name}
                className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
              />
            )}
            <div className="flex-1 min-w-0">
              <button
                onClick={handleVenueClick}
                className="text-lg font-bold text-brand-primary hover:text-brand-primary-dark mb-2 text-left transition-colors flex items-center gap-1 group"
              >
                {venue.name}
                <ExternalLink className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
              <button
                onClick={handleAddressClick}
                className="flex items-start gap-2 text-text-secondary hover:text-brand-primary transition-colors text-left"
              >
                <MapPin className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span className="text-sm">
                  {venue.address}
                  {venue.city && venue.state && ` - ${venue.city}, ${venue.state}`}
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* Occurrence Info */}
        <div className="space-y-4">
          <h3 className="text-lg font-bold text-text-primary">
            Informações do Rotativo
          </h3>

          {/* Modalidade */}
          <div>
            <span className="inline-block px-3 py-1 bg-brand-primary/10 text-brand-primary rounded-full text-sm font-medium">
              {modalityLabel}
            </span>
          </div>

          {/* Data e Horário */}
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Calendar className="h-5 w-5 text-text-secondary" />
              <div className="font-medium text-text-primary">
                {formatDateForUI(occurrence.date, 'long')}
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Clock className="h-5 w-5 text-text-secondary" />
              <div className="text-text-primary">
                {formatTimeForUI(occurrence.startsAt)} - {formatTimeForUI(occurrence.endsAt)}
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-border-light" />

          {/* Participants */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-text-secondary" />
                <span className="text-text-secondary">
                  {occurrence.totalParticipants}
                  {isUnlimited ? ' pessoas' : `/${occurrence.maxPeople} pessoas`}
                </span>
              </div>

              {!isUnlimited && (
                <div className="flex items-center gap-2">
                  <TrendingUp className={`h-5 w-5 ${isFull ? 'text-red-500' : isAlmostFull ? 'text-orange-500' : 'text-green-500'}`} />
                  <span className={`font-medium ${isFull ? 'text-red-600' : isAlmostFull ? 'text-orange-600' : 'text-green-600'}`}>
                    {isFull ? 'Lotado' : `${occurrence.availableSpots} ${occurrence.availableSpots === 1 ? 'vaga' : 'vagas'}`}
                  </span>
                </div>
              )}

              {isUnlimited && (
                <span className="text-green-600 font-medium">
                  Vagas ilimitadas
                </span>
              )}
            </div>

            {!isUnlimited && (
              <div className={`px-3 py-2 rounded-lg text-sm ${
                isFull
                  ? 'bg-red-100 text-red-700'
                  : isAlmostFull
                  ? 'bg-orange-100 text-orange-700'
                  : 'bg-green-100 text-green-700'
              }`}>
                {occupancyPercentage}% ocupado
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="border-t border-border-light" />

          {/* Price */}
          <div>
            <div className="text-sm text-text-secondary mb-1">Valor por pessoa</div>
            <div className="text-3xl font-bold text-brand-primary">
              {formatCurrency(occurrence.pricePerPersonCents)}
            </div>
          </div>

          {/* Participants */}
          {occurrence.participants && occurrence.participants.length > 0 && (
            <>
              {/* Divider */}
              <div className="border-t border-border-light" />

              <div>
                <h4 className="text-md font-bold text-text-primary mb-3">
                  Participantes ({occurrence.participants.length})
                </h4>
                <div className="border border-border-light rounded-lg">
                  <div className="divide-y divide-border-light">
                    {occurrence.participants.map(participant => (
                      <div
                        key={participant.id}
                        className={`flex items-center gap-3 p-3 hover:bg-gray-50 transition-colors ${
                          participant.isCurrentUser ? 'bg-brand-primary/5' : ''
                        }`}
                      >
                        {participant.userAvatarUrl ? (
                          <img
                            src={participant.userAvatarUrl}
                            alt={participant.userName}
                            className="w-10 h-10 rounded-full object-cover border-2 border-gray-200 flex-shrink-0"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-brand-primary/10 border-2 border-brand-primary/20 flex items-center justify-center flex-shrink-0">
                            <span className="text-brand-primary font-semibold">
                              {participant.userName.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        )}
                        <div className="flex items-center gap-2 flex-1">
                          <span className="text-sm text-text-primary font-medium">
                            {participant.userName}
                          </span>
                          {participant.isCurrentUser && (
                            <span className="px-2 py-0.5 text-xs font-semibold bg-brand-primary text-white rounded-full">
                              Você
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
        </div>

        {/* Fixed Actions */}
        <div className="pt-4 mt-4 border-t border-border-light flex-shrink-0">
          {occurrence.isUserParticipating ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-green-600 mb-2">
                <div className="w-2 h-2 bg-green-600 rounded-full" />
                <span className="font-medium">Você está participando.</span>
              </div>
              <button
                onClick={handleCancelParticipation}
                disabled={actionLoading}
                className="w-full py-3 px-4 rounded-lg border-2 border-red-500 text-red-500 font-medium hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {actionLoading && <Loader2 className="h-5 w-5 animate-spin" />}
                Cancelar Participação
              </button>
            </div>
          ) : (
            <button
              onClick={handleParticipate}
              disabled={actionLoading || (isFull && !isUnlimited)}
              className="w-full py-3 px-4 rounded-lg bg-brand-primary text-white font-medium hover:bg-brand-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              {actionLoading && <Loader2 className="h-5 w-5 animate-spin" />}
              {isFull && !isUnlimited ? 'Lotado' : 'Participar'}
            </button>
          )}
        </div>
      </div>
      <AuthModal
        open={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onSuccess={() => setShowLoginModal(false)}
      />
    </Modal>
  )
}
